// "Send email" affordance for an outgoing action item (Meetings + Action Items
// pages). Opens a compose/preview dialog driven entirely by the
// action_item_emails row (migration 0045): the worker drafts + sends; this
// component only writes owner intent (create draft request → edit / re-prompt →
// Send) and polls the row for the result.
//
// N1: clicking Send sets status='send_requested' — the ONLY transition that
// authorizes the worker to send. Nothing goes out without this explicit click.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Mail,
  Send,
  Loader2,
  Pencil,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import {
  TERMINAL_STATUSES,
  parseEmails,
  type ActionItemEmailStatus,
  type EmailCandidate,
} from '@/lib/actionItemEmail';

type EmailRow = {
  id: string;
  status: ActionItemEmailStatus;
  to_emails: string[];
  subject: string | null;
  body: string | null;
  engine: 'cli' | 'api' | null;
  draft_meta: { placeholders?: string[] } | null;
  error: string | null;
  updated_at: string | null;
};

const ROW_SELECT = 'id, status, to_emails, subject, body, engine, draft_meta, error, updated_at';

export function SendActionItemEmailButton({
  actionItemId,
  coveredItemIds,
  sourceMeetingId,
  candidates,
  ownerId,
  compact = false,
  selfReminder = false,
}: {
  /** Anchor action item (first covered). Null = meeting-anchored follow-up
   *  to a participant with no anchor item (migration 0048). */
  actionItemId: string | null;
  /** Every item this one email covers (the participant's open items). */
  coveredItemIds?: string[];
  sourceMeetingId: string | null;
  candidates: EmailCandidate[];
  ownerId: string;
  /** Icon-only button for dense rows; labelled otherwise. */
  compact?: boolean;
  /** True when the recipient is the owner himself — reminder framing, not follow-up. */
  selfReminder?: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [rowId, setRowId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [seededAt, setSeededAt] = useState<string | null>(null);
  // Guards the resend-after-error path: a send that failed AFTER AgentMail may
  // have delivered must never be resent on one click (no idempotency key exists).
  const [confirmResend, setConfirmResend] = useState(false);

  const [toText, setToText] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [instruction, setInstruction] = useState('');

  const { data: row } = useQuery<EmailRow | null>({
    queryKey: ['action_item_email', rowId],
    enabled: open && rowId !== null,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('action_item_emails')
        .select(ROW_SELECT)
        .eq('id', rowId)
        .maybeSingle();
      if (error) throw error;
      return (data as EmailRow | null) ?? null;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && TERMINAL_STATUSES.has(status) ? false : 1200;
    },
  });

  // Seed the editable fields from each new draft (keyed on updated_at so a
  // re-prompt's fresh draft re-seeds, but manual edits between drafts are kept).
  useEffect(() => {
    if (!row || row.status !== 'draft_ready') return;
    if (row.updated_at === seededAt) return;
    setToText(row.to_emails.join(', '));
    setSubject(row.subject ?? '');
    setBody(row.body ?? '');
    setSeededAt(row.updated_at ?? null);
    setEditing(false);
  }, [row, seededAt]);

  const createDraft = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase
        .from('action_item_emails')
        .insert({
          owner_id: ownerId,
          action_item_id: actionItemId,
          covered_action_item_ids: coveredItemIds ?? (actionItemId ? [actionItemId] : []),
          source_meeting_id: sourceMeetingId,
          to_emails: candidates.map((c) => c.email),
          status: 'draft_requested',
        })
        .select('id')
        .single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: (id) => setRowId(id),
    onError: () => toast.error('Could not start the draft.'),
  });

  const revise = useMutation({
    mutationFn: async (nextInstruction: string | null) => {
      const patch: Record<string, unknown> = {
        instruction: nextInstruction,
        status: 'draft_requested',
        error: null,
      };
      // A content re-prompt (non-null instruction) must preserve the owner's
      // current recipient edits — the worker keeps to_emails untouched when an
      // instruction is set, so persist the edited set here or it's lost. Guarded
      // so a from-error retry (revise(null)) never wipes recipients to [].
      if (nextInstruction !== null) {
        const recipients = parseEmails(toText);
        if (recipients.length > 0) patch.to_emails = recipients;
      }
      const { error } = await supabase
        .from('action_item_emails')
        .update(patch)
        .eq('id', rowId)
        .eq('owner_id', ownerId);
      if (error) throw error;
    },
    onSuccess: () => {
      setInstruction('');
      qc.invalidateQueries({ queryKey: ['action_item_email', rowId] });
    },
    onError: () => toast.error('Could not request changes.'),
  });

  const send = useMutation({
    mutationFn: async () => {
      const recipients = parseEmails(toText);
      if (recipients.length === 0) throw new Error('no recipients');
      const { error } = await supabase
        .from('action_item_emails')
        .update({
          to_emails: recipients,
          subject: subject.trim(),
          body,
          status: 'send_requested',
          instruction: null,
        })
        .eq('id', rowId)
        .eq('owner_id', ownerId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['action_item_email', rowId] }),
    onError: (err) =>
      toast.error(
        err instanceof Error && err.message === 'no recipients'
          ? 'Add at least one recipient.'
          : 'Could not send.',
      ),
  });

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    if (next) {
      // Fresh compose session each open.
      setRowId(null);
      setSeededAt(null);
      setEditing(false);
      setInstruction('');
      setConfirmResend(false);
      createDraft.mutate();
    } else {
      // Abandon an un-sent draft so drafts don't accumulate (history = sent only).
      if (rowId && row && !['sent', 'sending', 'send_requested'].includes(row.status)) {
        supabase
          .from('action_item_emails')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', rowId)
          .eq('owner_id', ownerId)
          .then(undefined, () => {});
      }
    }
  }

  const status = row?.status;
  const busy = status === 'draft_requested' || status === 'drafting';
  const sending = status === 'sending' || status === 'send_requested';
  const placeholders = row?.draft_meta?.placeholders ?? [];

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => handleOpenChange(true)}
        aria-label={
          selfReminder
            ? 'Email yourself a reminder about your action items'
            : `Draft a follow-up email to ${candidates[0]?.name ?? candidates[0]?.email ?? 'the assignee'}`
        }
        title={
          selfReminder
            ? 'Email yourself a reminder'
            : `Follow up with ${candidates[0]?.name ?? candidates[0]?.email ?? 'the assignee'}`
        }
      >
        <Mail className="w-4 h-4" aria-hidden="true" />
        {!compact && <span className="ml-1.5">Email</span>}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selfReminder
                ? 'Email yourself a reminder'
                : `Follow up with ${candidates[0]?.name ?? candidates[0]?.email ?? 'recipient'}`}
            </DialogTitle>
            <DialogDescription>
              Your assistant drafts {selfReminder ? 'a reminder about your' : 'a follow-up on the'}{' '}
              action item
              {(coveredItemIds?.length ?? 1) > 1 ? 's' : ''} — review, revise, then send. It goes
              out from reverra@agentmail.to on your behalf (not your Gmail).
            </DialogDescription>
          </DialogHeader>

          {/* Couldn't even create the draft request (e.g. RLS / network). */}
          {createDraft.isError && !rowId && (
            <div className="py-6 space-y-3">
              <p className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                <span>Couldn’t start the draft. Please try again.</span>
              </p>
              <Button size="sm" variant="outline" onClick={() => createDraft.mutate()}>
                <RefreshCw className="w-4 h-4 mr-1.5" aria-hidden="true" />
                Try again
              </Button>
            </div>
          )}

          {/* Drafting / initial load */}
          {!createDraft.isError && (busy || !status) && (
            <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              <span>Drafting your email…</span>
            </div>
          )}

          {status === 'draft_error' && (
            <div className="py-6 space-y-3">
              <p className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                <span>Couldn’t draft that email. {row?.error}</span>
              </p>
              <Button size="sm" variant="outline" onClick={() => revise.mutate(null)}>
                <RefreshCw className="w-4 h-4 mr-1.5" aria-hidden="true" />
                Try again
              </Button>
            </div>
          )}

          {status === 'sent' && (
            <div className="py-8 space-y-2 text-center">
              <CheckCircle2 className="w-8 h-8 text-sage mx-auto" aria-hidden="true" />
              <p className="text-sm text-foreground">Sent to {row?.to_emails.join(', ')}.</p>
              <Button size="sm" onClick={() => handleOpenChange(false)}>
                Done
              </Button>
            </div>
          )}

          {status === 'send_error' && (
            <div className="py-6 space-y-3">
              <p className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                <span>Couldn’t confirm the send. {row?.error}</span>
              </p>
              <p className="text-[11px] text-attention">
                This email may already have been delivered. Resending will send it again — only do
                this if you’ve confirmed it didn’t arrive.
              </p>
              {!confirmResend ? (
                <Button size="sm" variant="outline" onClick={() => setConfirmResend(true)}>
                  <RefreshCw className="w-4 h-4 mr-1.5" aria-hidden="true" />
                  Resend…
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      setConfirmResend(false);
                      send.mutate();
                    }}
                  >
                    Yes, resend anyway
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmResend(false)}>
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          )}

          {sending && (
            <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              <span>Sending…</span>
            </div>
          )}

          {status === 'draft_ready' && (
            <div className="space-y-3">
              <FieldRow label="To">
                <Input
                  value={toText}
                  onChange={(e) => setToText(e.target.value)}
                  disabled={!editing}
                  className="text-sm"
                />
              </FieldRow>
              <FieldRow label="Subject">
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={!editing}
                  className="text-sm"
                />
              </FieldRow>
              <FieldRow label="Body">
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  disabled={!editing}
                  rows={9}
                  className="text-sm resize-y"
                />
              </FieldRow>

              {placeholders.length > 0 && (
                <p className="text-[11px] text-attention">
                  Fill in before sending: {placeholders.join(', ')}
                </p>
              )}
              <p className="text-[11px] text-muted-foreground">
                {row?.engine === 'cli' ? 'Drafted free via Claude Code.' : 'Drafted via Claude.'}
              </p>

              {/* Ask-AI-to-revise box */}
              <div className="flex items-center gap-2 pt-1">
                <Input
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  placeholder="Ask AI to revise — e.g. “make it shorter”"
                  className="text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && instruction.trim()) revise.mutate(instruction.trim());
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!instruction.trim() || revise.isPending}
                  onClick={() => revise.mutate(instruction.trim())}
                >
                  <Sparkles className="w-4 h-4 mr-1.5" aria-hidden="true" />
                  Revise
                </Button>
              </div>

              <div className="flex items-center justify-between pt-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing((v) => !v)}
                  aria-pressed={editing}
                >
                  <Pencil className="w-4 h-4 mr-1.5" aria-hidden="true" />
                  {editing ? 'Done editing' : 'Edit manually'}
                </Button>
                <Button size="sm" disabled={send.isPending} onClick={() => send.mutate()}>
                  <Send className="w-4 h-4 mr-1.5" aria-hidden="true" />
                  Send
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
